import { PlaceType, type PlaceDto, TripMode, LegDto } from '@/types/webapi';
import { differenceInSeconds, isDate } from 'date-fns';
import { isUndefined, isNull, isEmpty, isObject } from 'lodash-es';
import { isSameLocalDate, toLocaleShortDateTimeString, toLocaleTimeString } from '@geta/kolumbus-frontend/composables';

/**
 * Determines whether a trip leg is a transfer between quays.
 * @param legDto Trip leg object
 * @returns True if leg is a transfer, false otherwise
 */
export function isTransfer(legDto: { from: PlaceDto; to: PlaceDto; tripMode: TripMode }): boolean {
    return (
        !isUndefined(legDto) &&
        !isNull(legDto) &&
        !isEmpty(legDto) &&
        isObject(legDto) &&
        legDto.from.type === PlaceType.TransitStation &&
        legDto.to.type === PlaceType.TransitStation &&
        (legDto.tripMode === TripMode.Foot || legDto.tripMode === TripMode.Bicycle)
    );
}

/**
 * Get the transfer time between two legs
 * @param prev - The previous leg
 * @param next - The next leg
 * @param curr - The current leg
 * @returns The transfer time in seconds
 */
export function getTransferTime(prev: LegDto, next: LegDto, curr?: Partial<LegDto>) {
    if (!prev || !next) return 0;

    const prevEndTime = prev.isEndingSoon ? prev.expectedEndTime : prev.aimedEndTime;
    const nextStartTime = next.isStartingSoon ? next.expectedStartTime : next.aimedStartTime;

    if (!isDate(prevEndTime) || !isDate(nextStartTime)) return 0;

    return differenceInSeconds(nextStartTime, prevEndTime) - (curr?.duration ?? 0);
}

/**
 * Formats a transit start time, prefixing it with a short date when it falls on a
 * different calendar day than the given reference instant (e.g. the previous leg's end).
 * @param startInstant - The instant to format
 * @param referenceInstant - The instant to compare against for a day change (e.g. previous leg's end)
 * @returns Formatted time string, or date-prefixed time string if the day differs
 */
export function formatTransitStartTime(startInstant?: Date, referenceInstant?: Date): string {
    if (!startInstant) return '';

    const showDate = !!referenceInstant && !isSameLocalDate(startInstant, referenceInstant);
    return showDate ? toLocaleShortDateTimeString(startInstant) : toLocaleTimeString(startInstant);
}
